import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, PlayCircle, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useMatch, playersOf } from "../lib/useMatch";
import { TacticalHeader } from "../components/MatchHeader";
import { EventTimeline } from "../components/EventTimeline";
import { Pitch, layoutPositions } from "../components/Pitch";
import { CardAlert, DeviceSimulator } from "../components/Device";
import { PageHeader, Panel, Spinner, IconBox, Empty } from "../components/kit";
import { Button } from "../components/ui/button";

const CARD = { yellow: "yellow", second_yellow: "red", red: "red" };

export const DemoLaunchCard = () => (
  <Link to="/demo" data-testid="start-guided-demo-btn" className="tro-card flex items-center gap-3 p-4 transition-colors hover:border-[#171B20]">
    <IconBox icon={PlayCircle} active />
    <div className="min-w-0 flex-1"><div className="text-base font-extrabold">Start guided demo</div><div className="text-xs text-[#6C757D]">The full match story from yellow card to league validation, one step at a time.</div></div>
    <ChevronRight className="h-5 w-5 text-[#6C757D]" />
  </Link>
);

function Stage({ id, alert, setAlert, mode }) {
  const room = useMatch(id);
  const m = room.match;
  if (!m) return <Spinner />;
  const P = playersOf(m, m.home.team_id);
  const pos = layoutPositions(P.lineup, room.events, m.tactics?.[m.home.team_id]);
  return (
    <div className="min-w-0 space-y-3" data-testid="demo-stage">
      <TacticalHeader match={m} elapsed={room.elapsed} conn={room.conn} queue={room.queue} />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_200px]">
        <div className="tro-card p-2"><div className="tro-label mb-1 px-1">{m.home.name} · tactical pitch</div><Pitch players={P.onField} positions={pos} state={m.player_state} size={44} testid="demo-pitch" /></div>
        <div className="tro-card flex flex-col items-center p-3"><div className="tro-label mb-2">TRO Referee One</div><DeviceSimulator mode={mode} small /></div>
      </div>
      <Panel title="Official match record" testid="demo-timeline"><EventTimeline compact events={room.events.slice(-8)} match={m} /></Panel>
      <CardAlert alert={alert} onClose={() => setAlert(null)} />
    </div>
  );
}

export default function DemoWalkthrough() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);
  const [mode, setMode] = useState("normal");
  const [denied, setDenied] = useState(null);
  const load = useCallback(() => api.get("/demo/walkthrough").then((r) => setW(r.data)).catch((e) => setDenied(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (denied) return <Empty testid="demo-access-denied" text={denied} />;
  if (!w) return <Spinner />;
  const s = w.steps[w.step];
  const done = w.step >= w.steps.length - 1;
  const next = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/walkthrough/next");
      const e = data.event;
      if (e && CARD[e.type]) {
        const { data: m } = await api.get(`/matches/${w.match_id}`);
        const p = m.players[e.player_id];
        setMode(CARD[e.type]);
        setAlert({ color: CARD[e.type], player: { ...p, name: `${p.first_name} ${p.last_name}`, club: e.team_id === m.home.team_id ? m.home.name : m.away.name }, minute: e.minute, second: e.second, event_type: e.type });
      } else setMode("normal");
      setW({ ...w, step: data.step });
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const restart = async () => {
    setBusy(true);
    try { await api.post("/demo/walkthrough/reset"); setMode("normal"); setW({ ...w, step: 0 }); toast.success("Demo match reset to 2–1 at 67:24"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="demo-walkthrough">
      <PageHeader title="Guided demo" sub="Riverside FC vs City United · every step runs on the real demo match">
        <Button variant="outline" className="h-11 rounded-[14px]" disabled={busy} onClick={restart} data-testid="demo-restart-btn"><RotateCcw className="h-4 w-4" />Restart</Button>
      </PageHeader>
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[400px_minmax(0,1fr)]">
          <div className="tro-card p-4 xl:col-start-1 xl:row-start-1" data-testid="demo-current-step">
            <div className="flex items-center justify-between"><span className="tro-label" data-testid="demo-progress">Step {w.step + 1} of {w.steps.length}</span><span className="rounded-full bg-[#171B20] px-2.5 py-1 text-[10px] font-extrabold uppercase text-white" data-testid="demo-step-role">{s.role}</span></div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E7EAEE]"><div className="h-full rounded-full bg-[#171B20] transition-[width] duration-500" style={{ width: `${((w.step + 1) / w.steps.length) * 100}%` }} /></div>
            <h2 className="mt-3 text-xl font-extrabold" data-testid="demo-step-title">{s.title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#343A40]" data-testid="demo-step-caption">{s.caption}</p>
            {!done && <div className="mt-3 rounded-[14px] bg-[#F7F8FA] p-3 text-xs"><span className="tro-label">Next</span><div className="mt-0.5 font-bold">{w.steps[w.step + 1].title} · {w.steps[w.step + 1].role}</div></div>}
            <Button className="mt-3 h-14 w-full rounded-[14px] text-base font-extrabold" disabled={busy || done} onClick={next} data-testid="demo-next-btn">{done ? "Walkthrough complete" : busy ? "Working…" : <>Next<ChevronRight className="h-5 w-5" /></>}</Button>
            {done && <Link to={`/matches/${w.match_id}`} className="mt-2 flex items-center justify-center gap-1 text-sm font-bold underline" data-testid="demo-open-record"><ExternalLink className="h-4 w-4" />Open the official match record</Link>}
          </div>
          <div className="min-w-0 xl:col-start-2 xl:row-span-2 xl:row-start-1"><Stage id={w.match_id} alert={alert} setAlert={setAlert} mode={mode} /></div>
          <ol className="tro-card space-y-0.5 p-2 xl:col-start-1 xl:row-start-2" data-testid="demo-step-list">{w.steps.map((x, i) => (
            <li key={x.key} data-testid={`demo-step-${x.key}`} className={`flex items-center gap-2 rounded-[12px] px-2 py-1.5 text-sm ${i === w.step ? "bg-[#171B20] text-white" : i < w.step ? "text-[#343A40]" : "text-[#6C757D]"}`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] text-[11px] font-extrabold ${i < w.step ? "bg-[#35865C] text-white" : i === w.step ? "bg-white text-[#171B20]" : "bg-[#E7EAEE]"}`}>{i < w.step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className="min-w-0 flex-1 truncate font-semibold">{x.title}</span><span className="shrink-0 text-[10px] font-bold uppercase opacity-70">{x.role}</span>
            </li>
          ))}</ol>
      </div>
    </div>
  );
}
